// Helper function to validate numeric input
function isValidNumber(input) {
  const num = parseFloat(input);
  return !isNaN(num) && num > 0;
}

// Calculate initial price
function getInitialPrice(ethPool, tokenPool) {
  return ethPool / tokenPool;
}

// Calculate distribution using actual AMM constant product formula (x * y = k)
function calculateAMMBasedDistribution(
  totalSupply, 
  initialEthPool, 
  initialTokenPool,
  minPercentage,
  maxPercentage, 
  targetPercentage
) {
  // Calculate key parameters
  const initialPrice = getInitialPrice(initialEthPool, initialTokenPool);
  const tokensToDistribute = totalSupply * (targetPercentage / 100);
  
  // Create wallet sizes that grow exponentially
  const walletSizes = [];
  const baseAmount = 1000000; // Start with 1M tokens
  const growthRate = 1.15; // 15% growth between wallets
  
  let currentAmount = baseAmount;
  let remainingTokens = tokensToDistribute;
  
  // Generate wallet sizes with exponential growth and random max per wallet
  while (remainingTokens > 0) {
    // For each wallet, pick a random max percentage between min and max
    const randomPercentage = Math.random() * (maxPercentage - minPercentage) + minPercentage;
    const randomMaxTokens = totalSupply * (randomPercentage / 100);
    const adjustedAmount = Math.min(currentAmount, randomMaxTokens, remainingTokens);
    if (adjustedAmount <= 0) break;
    walletSizes.push(adjustedAmount);
    remainingTokens -= adjustedAmount;
    // Increase for next wallet
    currentAmount = Math.min(currentAmount * growthRate, randomMaxTokens);
  }
  
  // Initialize pool state
  let currentEthPool = initialEthPool;
  let currentTokenPool = initialTokenPool;
  const constantK = currentEthPool * currentTokenPool; // The constant product k = x * y
  
  // Create wallets with prices from the AMM formula
  const wallets = [];
  let totalTokensDistributed = 0;
  let totalEthUsed = 0;
  
  for (let i = 0; i < walletSizes.length; i++) {
    const tokensForWallet = walletSizes[i];
    
    // Calculate ETH required using the constant product formula
    // For token purchase: dy = y - k/(x+dx)
    // Where:
    //   x = current token pool
    //   y = current ETH pool
    //   dx = tokens being purchased
    //   dy = ETH required
    //   k = constant product
    
    // New token pool after purchase
    const newTokenPool = currentTokenPool - tokensForWallet;
    
    // Required ETH based on constant product formula
    const newEthPool = constantK / newTokenPool;
    const ethRequired = newEthPool - currentEthPool;
    
    // Calculate effective price per token
    const pricePerToken = ethRequired / tokensForWallet;
    
    // Calculate price impact - this is more accurate than the model-based version
    const priceImpact = ((pricePerToken / initialPrice) - 1) * 100;
    
    // Add wallet to distribution
    wallets.push({
      tokens: tokensForWallet,
      eth: ethRequired,
      pricePerToken: pricePerToken,
      tokenPercentage: (tokensForWallet / totalSupply) * 100,
      priceImpact: priceImpact
    });
    
    // Update pool state for next calculation
    currentEthPool = newEthPool;
    currentTokenPool = newTokenPool;
    
    // Update totals
    totalTokensDistributed += tokensForWallet;
    totalEthUsed += ethRequired;
  }
  
  // Calculate final metrics
  const finalPrice = currentEthPool / currentTokenPool;
  const totalPriceImpact = ((finalPrice / initialPrice) - 1) * 100;
  const avgPricePerToken = totalEthUsed / totalTokensDistributed;
  
  return {
    numWallets: wallets.length,
    wallets: wallets,
    totalTokensDistributed: totalTokensDistributed,
    totalEthUsed: totalEthUsed,
    initialPrice: initialPrice,
    finalPrice: finalPrice,
    totalPriceImpact: totalPriceImpact,
    avgPricePerToken: avgPricePerToken,
    totalPercentageDistributed: (totalTokensDistributed / totalSupply) * 100
  };
}

// Function to display distribution results
function displayResults(distribution, totalSupply, initialEthPool, initialTokenPool, isAMMModel) {
  console.log("\n===== DISTRIBUTION RESULTS =====");
  console.log(`Total Supply: ${totalSupply}`);
  console.log(`Initial ETH Pool: ${initialEthPool}`);
  console.log(`Initial Token Pool: ${initialTokenPool}`);
  console.log(`Initial Price: ${distribution.initialPrice.toFixed(8)} ETH per token`);
  console.log(`Model Type: ${isAMMModel ? "Realistic AMM (x*y=k)" : "Exponential Price Curve"}`);
  
  console.log(`\nDistribution Summary:`);
  console.log(`Number of Wallets: ${distribution.numWallets}`);
  console.log(`Total Tokens Distributed: ${distribution.totalTokensDistributed.toFixed(4)} (${distribution.totalPercentageDistributed.toFixed(2)}% of supply)`);
  console.log(`Total ETH Used: ${distribution.totalEthUsed.toFixed(6)}`);
  console.log(`Average Price Per Token: ${distribution.avgPricePerToken.toFixed(8)} ETH`);
  console.log(`Final Price: ${distribution.finalPrice.toFixed(8)} ETH per token`);
  console.log(`Total Price Impact: ${distribution.totalPriceImpact.toFixed(2)}%`);
  
  console.log("\nDetailed Wallet Breakdown (Low to High):");
  distribution.wallets.forEach((wallet, i) => {
    console.log(`Wallet ${i+1}:`);
    console.log(`  - Tokens: ${wallet.tokens.toFixed(4)} (${wallet.tokenPercentage.toFixed(4)}% of supply)`);
    console.log(`  - ETH Required: ${wallet.eth.toFixed(6)}`);
    console.log(`  - Price Per Token: ${wallet.pricePerToken.toFixed(8)} ETH`);
    console.log(`  - Cumulative Price Impact: ${wallet.priceImpact.toFixed(2)}%`);
  });
}

// Function to export distribution results to CSV
function exportToCSV(distribution, totalSupply, initialEthPool, initialTokenPool, filename) {
  try {
    // Create CSV header
    let csvContent = 'Wallet Number,Tokens,Token Percentage,ETH Required,Price Per Token,Cumulative Price Impact\n';
    
    // Add data for each wallet
    distribution.wallets.forEach((wallet, i) => {
      csvContent += `${i+1},${wallet.tokens.toFixed(4)},${wallet.tokenPercentage.toFixed(4)}%,${wallet.eth.toFixed(6)},${wallet.pricePerToken.toFixed(8)},${wallet.priceImpact.toFixed(2)}%\n`;
    });
    
    // Add summary data at the end
    csvContent += '\nDistribution Summary\n';
    csvContent += `Total Supply,${totalSupply}\n`;
    csvContent += `Initial ETH Pool,${initialEthPool}\n`;
    csvContent += `Initial Token Pool,${initialTokenPool}\n`;
    csvContent += `Initial Price,${distribution.initialPrice.toFixed(8)}\n`;
    csvContent += `Number of Wallets,${distribution.numWallets}\n`;
    csvContent += `Total Tokens Distributed,${distribution.totalTokensDistributed.toFixed(4)}\n`;
    csvContent += `Total Percentage Distributed,${distribution.totalPercentageDistributed.toFixed(2)}%\n`;
    csvContent += `Total ETH Used,${distribution.totalEthUsed.toFixed(6)}\n`;
    csvContent += `Average Price Per Token,${distribution.avgPricePerToken.toFixed(8)}\n`;
    csvContent += `Final Price,${distribution.finalPrice.toFixed(8)}\n`;
    csvContent += `Total Price Impact,${distribution.totalPriceImpact.toFixed(2)}%\n`;
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error(`Error exporting to CSV: ${error.message}`);
    throw error;
  }
}

// Function to update wallets.json with calculated ETH amounts
async function updateWalletsWithDistribution(distribution, gasBufferPercentage, existingWallets) {
  try {
    // Update wallets with calculated ETH amounts
    const updateCount = Math.min(existingWallets.length, distribution.numWallets);
    for (let i = 0; i < updateCount; i++) {
      // Calculate ETH amount with gas buffer
      const baseEthAmount = distribution.wallets[i].eth;
      const gasBuffer = baseEthAmount * (gasBufferPercentage / 100);
      const totalEthAmount = baseEthAmount + gasBuffer;
      
      existingWallets[i].buyAmount = parseFloat(totalEthAmount.toFixed(9)); // Using 9 decimals for ETH precision
    }

    // Save updated wallets back to file using fetch
    const response = await fetch('/api/update-wallets', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(existingWallets),
    });

    if (!response.ok) {
      throw new Error('Failed to update wallets');
    }

    return {
      success: true,
      message: `Successfully updated ${updateCount} wallets with buy amounts`,
      totalFunding: existingWallets.reduce((sum, wallet) => sum + (wallet.buyAmount || 0), 0)
    };
  } catch (error) {
    console.error(`Error updating wallets: ${error.message}`);
    throw error;
  }
}

export {
  calculateAMMBasedDistribution,
  displayResults,
  exportToCSV,
  updateWalletsWithDistribution,
  isValidNumber
}; 